import { sample, shuffle } from "lodash"

import { STEPS } from "../../../../enums/ateliers"
import { ITEM_TYPES } from "../config/constants"
import {
  DetectiveCluxItem,
  DetectiveCluxState,
  ItemType,
  Level,
  SelectionOutcome,
  SelectionRejection,
  Solution,
  Suspects,
} from "../config/interfaces"

/** État de départ : l'enquête n'est pas encore commencée */
export const createInitialState = (): DetectiveCluxState => ({
  step: STEPS.PRESENTATION,
  level: null,
  solution: null,
  deck: [],
  drawnCount: 0,
  viewedClueIndex: null,
  disabledTileIds: [],
  checkAnswers: true,
})

export const getItemsByType = (
  items: DetectiveCluxItem[],
  type: ItemType,
): DetectiveCluxItem[] => items.filter(item => item.type === type)

export const findItem = (
  items: DetectiveCluxItem[],
  id: string,
): DetectiveCluxItem | undefined => items.find(item => item.id === id)

/** Tire au hasard un élément de chaque famille : c'est la combinaison gagnante */
export const drawSolution = (items: DetectiveCluxItem[]): Solution => {
  const pickId = (type: ItemType): string => {
    const item = sample(getItemsByType(items, type))
    if (!item) {
      throw new Error(`Aucun élément de type « ${type} » dans la configuration`)
    }
    return item.id
  }

  return {
    personnage: pickId("personnage"),
    objet: pickId("objet"),
    lieu: pickId("lieu"),
  }
}

/** Pioche : tous les éléments sauf la solution, mélangés tous types confondus */
export const buildDeck = (
  items: DetectiveCluxItem[],
  solution: Solution,
): string[] => {
  const solutionIds = Object.values(solution)
  return shuffle(
    items.filter(item => !solutionIds.includes(item.id)).map(item => item.id),
  )
}

/** Démarre une nouvelle partie au niveau choisi, avec ou sans contrôle des réponses */
export const startGame = (
  items: DetectiveCluxItem[],
  level: Level,
  checkAnswers: boolean,
): DetectiveCluxState => {
  const solution = drawSolution(items)
  return {
    ...createInitialState(),
    step: STEPS.ATELIER,
    level,
    solution,
    checkAnswers,
    deck: buildDeck(items, solution),
  }
}

export const hasRemainingClues = (state: DetectiveCluxState): boolean =>
  state.drawnCount < state.deck.length

/** Retourne la carte suivante de la pioche et l'affiche */
export const drawClue = (state: DetectiveCluxState): DetectiveCluxState => {
  if (!hasRemainingClues(state)) {
    return state
  }
  return {
    ...state,
    viewedClueIndex: state.drawnCount,
    drawnCount: state.drawnCount + 1,
  }
}

/** Id de l'élément dont l'indice est affiché, ou null */
export const getViewedClueId = (state: DetectiveCluxState): string | null =>
  state.viewedClueIndex === null
    ? null
    : (state.deck[state.viewedClueIndex] ?? null)

export const canShowPreviousClue = (state: DetectiveCluxState): boolean =>
  state.viewedClueIndex !== null && state.viewedClueIndex > 0

export const canShowNextClue = (state: DetectiveCluxState): boolean =>
  state.viewedClueIndex !== null && state.viewedClueIndex < state.drawnCount - 1

/** Réaffiche l'indice pioché précédemment */
export const showPreviousClue = (
  state: DetectiveCluxState,
): DetectiveCluxState =>
  canShowPreviousClue(state) && state.viewedClueIndex !== null
    ? { ...state, viewedClueIndex: state.viewedClueIndex - 1 }
    : state

/** Affiche l'indice pioché suivant (sans piocher de nouvelle carte) */
export const showNextClue = (state: DetectiveCluxState): DetectiveCluxState =>
  canShowNextClue(state) && state.viewedClueIndex !== null
    ? { ...state, viewedClueIndex: state.viewedClueIndex + 1 }
    : state

/** Résultat d'un clic sur une tuile : le nouvel état et l'issue (pour les sons et le tremblement) */
export interface TileSelection {
  state: DetectiveCluxState
  outcome: SelectionOutcome
}

/** Le joueur ne peut pas écarter plus de tuiles qu'il n'a lu d'indices */
export const canDisableMoreTiles = (state: DetectiveCluxState): boolean =>
  state.disabledTileIds.length < state.drawnCount

const disableTile = (
  state: DetectiveCluxState,
  tileId: string,
): DetectiveCluxState => ({
  ...state,
  disabledTileIds: [...state.disabledTileIds, tileId],
})

const enableTile = (
  state: DetectiveCluxState,
  tileId: string,
): DetectiveCluxState => ({
  ...state,
  disabledTileIds: state.disabledTileIds.filter(id => id !== tileId),
})

const reject = (
  state: DetectiveCluxState,
  outcome: SelectionRejection,
): TileSelection => ({ state, outcome })

/**
 * Traite le clic sur une tuile. Dans les deux modes, le nombre de tuiles éteintes
 * ne peut pas dépasser le nombre d'indices lus.
 * - Mode contrôle : seule la tuile correspondant à l'indice affiché est acceptée,
 *   et elle reste éteinte.
 * - Mode libre : n'importe quelle tuile peut être éteinte, et rallumée à tout moment.
 */
export const selectTile = (
  state: DetectiveCluxState,
  tileId: string,
): TileSelection => {
  const isDisabled = state.disabledTileIds.includes(tileId)

  if (!state.checkAnswers) {
    if (isDisabled) {
      return { state: enableTile(state, tileId), outcome: "unselected" }
    }
    if (!canDisableMoreTiles(state)) {
      return reject(state, "maxReached")
    }
    return { state: disableTile(state, tileId), outcome: "selected" }
  }

  if (!canDisableMoreTiles(state)) {
    return reject(state, "maxReached")
  }
  const clueId = getViewedClueId(state)
  // L'indice affiché a déjà été résolu (le joueur est revenu dessus avec les flèches)
  if (clueId && state.disabledTileIds.includes(clueId)) {
    return reject(state, "alreadyFound")
  }
  if (tileId !== clueId) {
    return reject(state, "wrongTile")
  }
  return { state: disableTile(state, tileId), outcome: "correct" }
}

export const isRejection = (
  outcome: SelectionOutcome,
): outcome is SelectionRejection =>
  outcome === "maxReached" ||
  outcome === "alreadyFound" ||
  outcome === "wrongTile"

export const goToStep = (
  state: DetectiveCluxState,
  step: DetectiveCluxState["step"],
): DetectiveCluxState => ({ ...state, step })

/** Remet la partie à zéro et revient au choix du niveau */
export const restartGame = (): DetectiveCluxState => ({
  ...createInitialState(),
  step: STEPS.PARAMETRES,
})

export const getActiveItems = (
  items: DetectiveCluxItem[],
  disabledTileIds: string[],
): DetectiveCluxItem[] =>
  items.filter(item => !disabledTileIds.includes(item.id))

/**
 * Retourne les éléments choisis par le joueur s'il ne reste qu'une tuile active
 * par famille, sinon null.
 */
export const getSuspects = (
  items: DetectiveCluxItem[],
  disabledTileIds: string[],
): Suspects | null => {
  const activeItems = getActiveItems(items, disabledTileIds)
  const pickSingle = (type: ItemType): DetectiveCluxItem | null => {
    const activeOfType = getItemsByType(activeItems, type)
    return activeOfType.length === 1 ? activeOfType[0] : null
  }

  const personnage = pickSingle("personnage")
  const objet = pickSingle("objet")
  const lieu = pickSingle("lieu")
  if (!personnage || !objet || !lieu) {
    return null
  }
  return { personnage, objet, lieu }
}

/** Convertit la solution (ids) en éléments complets */
export const getSolutionItems = (
  items: DetectiveCluxItem[],
  solution: Solution,
): Suspects | null => {
  const personnage = findItem(items, solution.personnage)
  const objet = findItem(items, solution.objet)
  const lieu = findItem(items, solution.lieu)
  if (!personnage || !objet || !lieu) {
    return null
  }
  return { personnage, objet, lieu }
}

export const isSolutionFound = (
  suspects: Suspects | null,
  solution: Solution | null,
): boolean =>
  Boolean(suspects && solution) &&
  ITEM_TYPES.every(type => suspects?.[type].id === solution?.[type])
